import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Barking and Dagenham\'s AMBER DfT rating mean the council will pay my claim?',
    answer: 'Not automatically. AMBER means the borough performs below the best authorities on combined condition, spend and best-practice scorecards — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The council\'s June 2025 transparency report shows 48.6% of A-roads in RED condition at the 2024/25 survey, 76.9% of unclassified roads in amber or red, and projects ~2,000 pothole repairs in 2025/26. Those figures can support a claim when tied to your specific road and defect — they do not guarantee success on their own.',
  },
  {
    question: 'Barking and Dagenham has a GREEN spend rating — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend is £5.877m against a DfT capital allocation of £556,000. Section 58 is about the individual defect, not aggregate budgets. Overall, condition and best-practice ratings are all AMBER, and the DfT notes the overall and best-practice scorecards reflect incomplete carriageway treatment data.',
  },
  {
    question: 'Why is best practice AMBER when spend is GREEN?',
    answer: 'The DfT scorecards are separate. Barking and Dagenham\'s transparency report describes a risk-based asset management approach aligned to the Well-Managed Highway Infrastructure code, but the DfT still rates best practice AMBER for 2025/26 — partly because carriageway treatment data submitted to the government was incomplete. That gap between documented processes and the government\'s assessment is worth citing if the council relies on generic "we have a system" arguments rather than records for your street.',
  },
  {
    question: 'How bad are Barking and Dagenham\'s A-roads compared to other London boroughs?',
    answer: 'At the 2024/25 LoHEG survey, 48.6% of the borough\'s principal (A) roads were rated RED — major deterioration — against a London average of 29%. That is 19.6 percentage points worse, or roughly 68% higher than the capital average. The council itself states principal roads are in a higher state of disrepair than the London average, with around half of A roads in poor condition.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 276.7km — 83% of the borough\'s 334.8km carriageway network. The latest published survey shows 15.1% of U-roads in RED and 61.8% in amber (76.9% combined). B and C roads are surveyed bi-annually — half the network each year — so condition snapshots may not cover your street in the year of your incident. Prior reports via the council\'s online form and photos showing defect age matter more than borough-wide averages.',
  },
  {
    question: 'Why did pothole repairs surge from 1,446 to 2,500 in 2022/23?',
    answer: 'The council filled 1,446 potholes in 2021/22 and 2,500 in 2022/23 — a 73% rise. Volumes remained elevated at 2,445 in 2023/24 and 2,039 in 2024/25, with ~2,000 projected for 2025/26. The council attributes this to underlying structural deterioration and expects similar volumes until planned resurfacing catches up. That pattern indicates ongoing reactive workload across the network — not proof your specific defect was unavoidable, but evidence that defects are forming faster than preventative treatment alone prevents them.',
  },
  {
    question: 'How do I report a pothole to Barking and Dagenham Council?',
    answer: 'Report defects online at lbbd.gov.uk via the pothole and highway defect form. Defects on the A12, A13 and A406 (North Circular) must be reported to Transport for London, not the borough council. Prior reports of the same defect strengthen a claim by demonstrating notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BarkingAndDagenhamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Barking & Dagenham Pothole Claims | 48.6% A-Roads RED, GREEN Spend | Fixtyer"
        description="London Borough of Barking and Dagenham earns AMBER DfT scorecards on condition but GREEN on spend. Its June 2025 report shows 48.6% of A-roads in RED condition and ~2,000 pothole repairs projected for 2025/26. What that means for your Section 58 claim."
        canonical="/councils/barking-and-dagenham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Barking & Dagenham', url: '/councils/barking-and-dagenham' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="London Borough of Barking and Dagenham"
        councilSlug="barking-and-dagenham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/barking-and-dagenham"
        headline="Barking and Dagenham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Barking & Dagenham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">DfT caveat:</strong> The Department for Transport notes that
              Barking and Dagenham&apos;s overall and best-practice scorecards for 2025/26 are based on{' '}
              <strong className="text-white">incomplete carriageway treatment data</strong>.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            48.6% of A-Roads in RED Condition — Nearly Double the London Average
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            London Borough of Barking and Dagenham earns <strong className="text-amber-400">AMBER</strong> DfT
            scorecards on overall performance, condition and best practice — yet{' '}
            <strong className="text-emerald-400">GREEN</strong> on spend. Its June 2025 transparency report records{' '}
            <strong className="text-rose-400">48.6% of principal roads in RED condition</strong> at the 2024/25
            survey (London average: 29%), pothole repairs more than doubling to 2,500 in 2022/23, and{' '}
            <strong className="text-amber-400">76.9% of residential roads in amber or red</strong>. The council admits
            more investment is needed. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">~2,000</div>
            <div className="text-white font-medium mt-2">Pothole repairs projected for 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              After 2,039 fills in 2024/25 and a peak of 2,500 in 2022/23 — while historical resurfacing averaged
              just 5–7km a year against a 334.8km network, with 14.3km planned this exceptional funding year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the DfT scorecards actually show
          </h2>
          <p className="text-surface-600 mb-8">
            Four separate ratings — not a single headline — from the 2025/26 local road maintenance release
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">London Borough of Barking and Dagenham DfT road maintenance scorecards 2025/26</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left font-semibold text-brand-900">Scorecard</th>
                  <th className="px-6 py-4 text-center font-semibold text-brand-900">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Wider best practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 text-sm">
                <strong>What AMBER condition means:</strong> Principal roads are in worse shape than most London
                boroughs — 48.6% RED at the latest survey versus a 29% capital average. RED banding means major
                deterioration where maintenance should be considered, not merely cosmetic wear.
              </p>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-emerald-800 text-sm">
                <strong>Why spend is GREEN:</strong> Projected 2025/26 capital spend of £5.877m is more than ten
                times the £556,000 DfT allocation, with 88% classified as preventative. Aggregate investment does not
                prove the individual pothole that damaged your vehicle was inspected and repaired within reasonable
                intervals.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-amber-800 text-sm">
              <strong>Data caveat:</strong> The DfT notes Barking and Dagenham&apos;s overall and best-practice
              scorecards reflect incomplete carriageway treatment data. The council&apos;s June 2025 transparency
              report (V1.0) also states no A-road LoHEG surveys were carried out in 2020/21 or 2021/22 due to
              Covid-19 restrictions, and B/C plus U-roads are surveyed bi-annually — half the network each year.
              Do not treat network tables as proof of your street&apos;s condition on the day of your incident.
            </p>
          </div>

          <p className="mt-6 text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            334.8km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Barking and Dagenham&apos;s June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">334.8km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">276.7km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">83% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">30.1km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Principal road network (LoHEG surveyed)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">28.0km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Barking and Dagenham highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">620.0km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way (other)</td>
                  <td className="px-6 py-4 text-center font-medium">16km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">58.8km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridge structures and overline bridges</td>
                  <td className="px-6 py-4 text-center font-medium">23 structures, 18 overline bridges, 1 river barrage</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">19,258</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">16,786</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Principal (A) Roads carry most bus routes, vehicular traffic and cyclists across the Borough and form the principal road network. B & C roads serve as the main links between the A roads and the unclassified (U) Roads that are predominantly residential quieter roads."
            source="London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            LoHEG AI-led surveys on A-roads — B/C and U-roads measured bi-annually on alternating halves
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> A-road condition is collected through driven surveys where defects
              are identified using a trained AI model. B and C roads and unclassified roads are surveyed bi-annually,
              with half the network surveyed in one year and the other half the following year. No A-road surveys were
              done in 2020/21 or 2021/22 due to Covid-19 restrictions. Survey results reflect conditions at the time
              of assessment.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (30.1km) — surveyed annually via LoHEG</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono text-surface-400">2020/21–2021/22</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey (Covid-19)</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center text-rose-600">48.3%</td>
                    <td className="px-4 py-2 text-center">20.7%</td>
                    <td className="px-4 py-2 text-center">30.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">54.7%</td>
                    <td className="px-4 py-2 text-center">22.0%</td>
                    <td className="px-4 py-2 text-center">23.3%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">48.6%</td>
                    <td className="px-4 py-2 text-center font-bold">23.3%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">31.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm mb-4">
              RED share peaked at 54.7% in 2023/24 before easing to 48.6% — still nearly 68% higher than the 29%
              London average the council cites for 2024/25.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-surface-50 rounded-xl p-4 border border-surface-200">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-surface-600">London average (A-road RED)</span>
                  <span className="font-medium">29%</span>
                </div>
                <div className="w-full bg-surface-100 rounded-full h-3 mb-4">
                  <div className="bg-amber-500 h-3 rounded-full" style={{ width: '29%' }} />
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-surface-600">Barking and Dagenham</span>
                  <span className="font-bold text-rose-600">48.6%</span>
                </div>
                <div className="w-full bg-surface-100 rounded-full h-3">
                  <div className="bg-rose-500 h-3 rounded-full" style={{ width: '48.6%' }} />
                </div>
                <p className="text-rose-700 text-sm font-medium mt-3">+19.6 percentage points above London average</p>
              </div>
              <QuoteCard
                quote="Principal roads are in a higher state of disrepair than the London average of 29% in 2024/25, with around half of the A roads in poor condition."
                source="London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)"
              />
            </div>
          </div>

          {/* B&C and U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">B/C and U roads — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Road class</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-medium">B and C roads (28.0km)</td>
                    <td className="px-4 py-2 text-center">7.5%</td>
                    <td className="px-4 py-2 text-center">49.6%</td>
                    <td className="px-4 py-2 text-center">42.8%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-medium">U roads (276.7km)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">61.8%</td>
                    <td className="px-4 py-2 text-center font-bold">23.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              At the latest survey, 76.9% of unclassified residential roads were in amber or red condition — roughly
              213km of the borough&apos;s network. Only 23.1% of U-roads were green-rated. The council prioritises
              A-roads, bus routes and access to essential services under its risk-based hierarchy, meaning residential
              streets receive lower priority for planned maintenance.
            </p>
          </div>

          <QuoteCard
            quote="More investment is needed to counter the deterioration and bring the overall Borough Principal Road Network up to a good condition."
            source="London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but historical resurfacing output stayed at 5–7km a year until 2025/26
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Barking and Dagenham highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">km resurfaced</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">556</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">5,877</td>
                  <td className="px-4 py-3 text-center">785</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">14.3</td>
                  <td className="px-4 py-3 text-center text-emerald-400">88%</td>
                  <td className="px-4 py-3 text-center">12%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">171</td>
                  <td className="px-4 py-3 text-center">5,677</td>
                  <td className="px-4 py-3 text-center">746</td>
                  <td className="px-4 py-3 text-center">5.9</td>
                  <td className="px-4 py-3 text-center">88%</td>
                  <td className="px-4 py-3 text-center">12%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">171</td>
                  <td className="px-4 py-3 text-center">4,250</td>
                  <td className="px-4 py-3 text-center">746</td>
                  <td className="px-4 py-3 text-center">6.4</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">3,627</td>
                  <td className="px-4 py-3 text-center">746</td>
                  <td className="px-4 py-3 text-center">5.3</td>
                  <td className="px-4 py-3 text-center">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">3,870</td>
                  <td className="px-4 py-3 text-center">730</td>
                  <td className="px-4 py-3 text-center">6.6</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">3,380</td>
                  <td className="px-4 py-3 text-center">626</td>
                  <td className="px-4 py-3 text-center">5.4</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Capital spend has risen from £3.4m in 2020/21 to a projected £5.9m in 2025/26, while preventative
                maintenance share climbed to 88%. The council invests far beyond its DfT capital allocation — and
                plans 14.3km of carriageway treatment this year with DfT and TfL support, up from the historical 5–7km
                band.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Even with 88% preventative spend planned, the council budgets 12% for reactive defect response and
                expects ~2,000 pothole patches this year based on historical trends. High preventative percentages
                did not prevent A-road RED ratings near 50% or pothole volumes doubling in 2022/23. Spend volume
                does not prove every defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Repairs Doubled — and Stayed Elevated
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled from the council&apos;s June 2025 transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Barking and Dagenham estimated potholes filled 2021-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,446</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,500</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+72.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,445</td>
                  <td className="px-6 py-4 text-center text-amber-600">+69.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,039</td>
                  <td className="px-6 py-4 text-center text-amber-600">+41.0%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">~2,000</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="For carriageways, between 2,000 and 2,500 potholes have been repaired during the past 3 financial years, as seen in Table 3. Based on that trend, we expect a similar number for 2025/26. We are aiming to reduce this number by increasing our planned maintenance that addresses the underlying structural issues and aims to prevents potholes from forming."
              source="London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly six patches a day</h3>
              <p className="text-surface-600 text-sm">
                2,039 pothole repairs in 2024/25 works out to roughly 5.6 patches per day across 334.8km. The council
                acknowledges reactive patching addresses safety but not underlying structural failure — which is why
                it separates major resurfacing schemes from thin surfacing and pothole infills.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Barking and Dagenham says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual LoHEG AI-led driven surveys across London</li>
                <li>• <strong>B/C and U roads:</strong> bi-annual surveys — half the network each year</li>
                <li>• <strong>Safety inspections:</strong> from every two weeks to once a year, depending on road hierarchy</li>
                <li>• <strong>Defect reports:</strong> ad-hoc inspections following public reports or claims</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Risk-based hierarchy</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council sets inspection frequency and repair response times using a hierarchy based on usage levels,
                essential services, schools, town centres and other traffic generators — aligned with LoTAG and the
                Well-Managed Highway Infrastructure code.
              </p>
              <p className="text-surface-600 text-sm">
                Works programmes prioritise engineering need, asset condition and social benefit — meaning residential
                streets with lower hierarchy scores may wait longer between surveys and planned treatments.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Barking and Dagenham
              must maintain public highways — a duty the council cites explicitly. To defend a claim under{' '}
              <strong className="text-white">Section 58</strong>, it must show a reasonable system for inspecting and
              repairing the specific defect — not just publish an AMBER DfT scorecard or a GREEN spend label.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the bi-annual B/C or U-road survey cycle — and had it been surveyed recently?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (council portal, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="We aim to prioritise planned improvements; however, we have a statutory duty under the Highways Act (1980) to keep the highway safe and accessible to the public. This means we will have to respond to defects on our network when they appear."
            source="London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Barking and Dagenham says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">14.3km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Total carriageway resurfacing</div>
              <div className="text-xs text-emerald-700 mt-1">Up from historical 5–7km band</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">8.2km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">A-road programme</div>
              <div className="text-xs text-rose-700 mt-1">~25% of principal network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">6.1km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Non-principal resurfacing</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">~2,000</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes expected</div>
            </div>
          </div>

          <QuoteCard
            quote="This year, 8.2km of A roads are in the works programme, which amounts to around 25% of the total principal road network."
            source="London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)"
          />

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Even at the exceptional 14.3km resurfacing programme, the borough would treat roughly 4.3% of its
              334.8km carriageway network in a single year. The remaining 96%+ relies on reactive patching, routine
              inspections and the bi-annual condition survey cycle — on a network where 76.9% of U-roads were already
              amber or red at the last published survey.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Barking and Dagenham invests beyond its DfT allocation — but condition data tells a mixed story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £5.877m capital vs £556k DfT allocation projected
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 88% preventative spend planned for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Exceptional 14.3km resurfacing programme — well above historical 5–7km
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented risk-based asset management aligned to WMHI code
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED share eased from 54.7% (2023/24) to 48.6% (2024/25)
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing inspection hierarchy and increased 2025/26 investment.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall, condition and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT incomplete carriageway treatment data flag on overall and best-practice ratings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 48.6% A-road RED — 19.6 points above the 29% London average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 276.7km of U-roads — 83% of network — with 76.9% in amber or red at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole fills surged 73% in 2022/23 and remain ~40% above 2021/22 baseline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits more investment is needed to counter deterioration
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend but AMBER condition, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road type</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond bi-annual survey cycles</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the bi-annual survey gap and 76.9% amber/red snapshot are structural arguments</li>
              <li>• Whether the council can produce inspection records for your street, not just borough-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from Barking and
              Dagenham&apos;s own June 2025 transparency data — including the DfT incomplete-treatment-data caveat — where
              it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Barking and Dagenham Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. Defects on the A12, A13 and A406 must be reported
            to Transport for London.
          </p>
          <a
            href="https://www.lbbd.gov.uk/report-road-and-pavement-problems/report-pothole-or-road-or-pavement-defect"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — lbbd.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Barking and Dagenham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            AMBER condition with GREEN spend demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No bi-annual U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No DfT incomplete-treatment-data citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 48.6% A-road RED condition documented</li>
                <li>✅ London 29% average comparison cited</li>
                <li>✅ 2,500 pothole peak and sustained elevation referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Barking and Dagenham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=barking-and-dagenham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Barking and Dagenham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.lbbd.gov.uk/roads-and-pavements/roads-traffic-and-transport/highways-surveys-and-highway-inspectors"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Barking and Dagenham Local Highways Maintenance Transparency Report (June 2025, V1.0)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
